/**
 * Generic React Native -> WebView event bridge.
 *
 * The mobile app embeds BTNet web pages inside React Native WebViews. When data
 * changes on the native side (e.g. the user clocks in/out on a native screen),
 * RN notifies the embedded web page by injecting a single global call:
 *
 *   window.__btReceiveRNEvent(key, payload); // payload may be `undefined`
 *
 * The web side owns that global. This module installs it once and fans each
 * incoming event out to the handlers subscribed to its `key`. The bridge is
 * intentionally generic: new features subscribe to new keys with no changes
 * here.
 *
 * The RN side optional-chains the call (`window.__btReceiveRNEvent?.(...)`), so
 * an event fired before the global is installed is a silent no-op on the native
 * side. An event that arrives after install but before any handler has
 * subscribed for its key is dropped — events are not buffered for replay.
 *
 * The `Window` augmentation is kept private to this module (matching the other
 * bridges in this package) so the bridge shape doesn't leak into the global
 * namespace of every package consumer.
 */
import type {
  MobileFunctions,
  MobileToWebEventKey,
} from "@bt-mobile/shared-contracts";

export type RNEventHandler = (payload: unknown) => void;

function getRNEventBridgeWindow(): MobileFunctions {
  return window as unknown as MobileFunctions;
}

const subscribers = new Map<MobileToWebEventKey, Set<RNEventHandler>>();

let installed = false;

function receiveRNEvent(key: MobileToWebEventKey, payload: unknown): void {
  const handlers = subscribers.get(key);

  if (!handlers || handlers.size === 0) {
    return;
  }

  handlers.forEach((handler) => {
    try {
      handler(payload);
    } catch (err) {
      // One bad subscriber shouldn't break the others or the bridge.
      console.error(`[RNEventBridge] handler for "${key}" threw`, err);
    }
  });
}

/**
 * Installs `window.__btReceiveRNEvent` once. Idempotent — safe to call at app
 * bootstrap and lazily from `subscribeRNEvent`.
 */
export function installRNEventBridge(): void {
  if (installed) return;
  installed = true;
  getRNEventBridgeWindow().__btReceiveRNEvent = receiveRNEvent;
}

/**
 * Subscribes `handler` to RN events with the given `key`. Returns an
 * unsubscribe function. Guarantees the global is installed.
 */
export function subscribeRNEvent(
  key: MobileToWebEventKey,
  handler: RNEventHandler,
): () => void {
  installRNEventBridge();

  let handlers = subscribers.get(key);
  if (!handlers) {
    handlers = new Set();
    subscribers.set(key, handlers);
  }
  handlers.add(handler);

  return () => {
    handlers.delete(handler);
    if (handlers.size === 0) {
      subscribers.delete(key);
    }
  };
}
